import type { CSSProperties } from 'react';
import { useEffect, useRef, useState } from 'react';

const SPARK_COUNT = 14;
const BURST_DURATION_MS = 1600; // 0.5x speed (slower than typical)
const PAUSE_BETWEEN_MS = 1200;
const CYCLE_MS = BURST_DURATION_MS + PAUSE_BETWEEN_MS;

interface Spark {
  angle: number;
  distance: number;
  size: number;
  delay: number;
  hue: number; // 20-40 range (orange-yellow)
}

function generateSparks(): Spark[] {
  return Array.from({ length: SPARK_COUNT }, () => ({
    angle: Math.random() * 360,
    distance: 30 + Math.random() * 50,
    size: 1.5 + Math.random() * 2.5,
    delay: Math.random() * 0.3,
    hue: 20 + Math.random() * 25,
  }));
}

export function SparkleAnimation() {
  const [sparks, setSparks] = useState<Spark[]>(generateSparks);
  const [jitter, setJitter] = useState({ x: 0, y: 0 });
  const [burstKey, setBurstKey] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setSparks(generateSparks());
      setJitter({
        x: (Math.random() - 0.5) * 24,
        y: (Math.random() - 0.5) * 24,
      });
      setBurstKey((k) => k + 1);
    }, CYCLE_MS);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  return (
    <div
      className="relative w-28 h-28 mb-6"
      aria-hidden="true"
      style={{ transform: `translate(${jitter.x}px, ${jitter.y}px)` }}
    >
      {/* Ambient glow at center */}
      <div
        className="absolute inset-0 m-auto w-8 h-8 rounded-full"
        style={{
          background: 'radial-gradient(circle, rgba(249,114,22,0.5) 0%, transparent 70%)',
          filter: 'blur(6px)',
          animation: 'pulse 2s ease-in-out infinite',
        }}
      />
      {/* Spark particles */}
      {sparks.map((spark, i) => {
        const rad = (spark.angle * Math.PI) / 180;
        const dx = Math.cos(rad);
        const dy = Math.sin(rad);

        const style: CSSProperties = {
          '--dx': dx,
          '--dy': dy,
          '--dist': `${spark.distance}px`,
          position: 'absolute',
          left: '50%',
          top: '50%',
          width: `${spark.size}px`,
          height: `${spark.size}px`,
          borderRadius: '50%',
          background: `hsl(${spark.hue}, 100%, 85%)`,
          boxShadow: `0 0 ${spark.size * 2}px hsl(${spark.hue}, 100%, 70%), 0 0 ${spark.size}px white`,
          animationName: 'spark-burst',
          animationDuration: `${BURST_DURATION_MS}ms`,
          animationTimingFunction: 'cubic-bezier(0.25, 0.1, 0.25, 1)',
          animationDelay: `${spark.delay}s`,
          animationFillMode: 'forwards',
          opacity: 0,
        } as CSSProperties;

        return <div key={`${burstKey}-${i}`} style={style} />;
      })}
    </div>
  );
}
